// Phase 23 (23f) — agreement-acceptance viewer for a caregiver / homecare
// detail page. Self-fetches GET /admin/agreements/acceptances/{userId} and
// renders each signed acceptance (type, version, typed legal name, IP, date)
// with a signed-PDF download and a re-render recovery action.
//
// Keyed on the USER id (not the caregiver/homecare row id) — acceptances are
// per-User, so both detail pages pass `userId` from their detail response.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader } from '@truecare/ui';
import { unwrap, messageOf } from '@truecare/api-client';
import { api } from '../api';

interface AcceptanceRow {
  id: string;
  agreementType: 'CAREGIVER_IC' | 'HOMECARE_CLIENT';
  subjectUserId: string;
  versionAccepted: string;
  contentHash: string;
  typedLegalName: string;
  ipAddress: string | null;
  acceptedAt: string;
  signedPdfUrl: string | null;
}

const TYPE_LABEL: Record<AcceptanceRow['agreementType'], string> = {
  CAREGIVER_IC: 'Independent Contractor',
  HOMECARE_CLIENT: 'Client Agreement',
};

export function AgreementAcceptancesCard({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const queryKey = ['admin', 'agreements', 'acceptances', userId] as const;

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/admin/agreements/acceptances/{userId}' as never, {
          params: { path: { userId } },
        } as never),
      ) as Promise<{ items: AcceptanceRow[] }>,
  });

  const reRender = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/api/v1/admin/agreements/acceptances/{id}/re-render' as never, {
          params: { path: { id } },
          body: {},
        } as never),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const items = listQuery.data?.items ?? [];

  return (
    <Card padding="lg" data-testid="agreement-acceptances-card">
      <CardHeader
        title="Signed agreements"
        description="Every agreement version this user has accepted — the legal evidence (typed signature, IP, timestamp, content hash) plus the signed PDF."
      />

      {listQuery.isError && (
        <Alert tone="danger" className="mt-3">
          {messageOf(listQuery.error, 'Could not load agreement acceptances.')}
        </Alert>
      )}
      {reRender.isError && (
        <Alert tone="danger" className="mt-3">
          {messageOf(reRender.error, 'Could not re-render the signed PDF.')}
        </Alert>
      )}

      {listQuery.isLoading ? (
        <p className="mt-4 text-sm text-ink-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 text-sm text-ink-muted" data-testid="agreement-acceptances-empty">
          No agreements signed yet.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm" data-testid="agreement-acceptances-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-muted">
                <th className="py-2 pr-4">Agreement</th>
                <th className="py-2 pr-4">Version</th>
                <th className="py-2 pr-4">Signed by</th>
                <th className="py-2 pr-4">IP</th>
                <th className="py-2 pr-4">Accepted</th>
                <th className="py-2 pr-4">Signed PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((a) => (
                <tr key={a.id} data-testid={`agreement-acceptance-row-${a.id}`}>
                  <td className="py-2 pr-4">{TYPE_LABEL[a.agreementType]}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{a.versionAccepted}</td>
                  <td className="py-2 pr-4">{a.typedLegalName}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{a.ipAddress ?? '—'}</td>
                  <td className="py-2 pr-4">{new Date(a.acceptedAt).toLocaleString()}</td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-3">
                      {a.signedPdfUrl ? (
                        <a
                          href={a.signedPdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-forest underline"
                          data-testid={`agreement-acceptance-pdf-${a.id}`}
                        >
                          Download
                        </a>
                      ) : (
                        <span className="text-xs text-ink-muted">Not rendered</span>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={reRender.isPending && reRender.variables === a.id}
                        onClick={() => reRender.mutate(a.id)}
                        data-testid={`agreement-acceptance-rerender-${a.id}`}
                      >
                        Re-render
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
